import React, { useEffect, useState } from "react";
import Sidebar from "components/Sidebar/Sidebar.js";
import config from "services/config";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Rocket, Plus, Send, Mail, Calendar, ToggleLeft, ToggleRight } from "lucide-react";

const CARD = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #e8ecf1",
  overflow: "hidden",
  cursor: "pointer",
  transition: "all 0.2s",
};

export function MarketingList() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${config.url}/marketing/list`, {
        headers: { Authorization: `Basic ${process.env.REACT_APP_BASIC_AUTH}` },
      })
      .then(({ data }) => {
        setCampaigns(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  return (
    <>
    <Sidebar />
    <div style={{ marginLeft: 210, padding: "24px 32px 32px", minHeight: "100vh", background: "#fff" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Rocket size={22} color="#0353a4" />
          <h1 style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", margin: 0 }}>
            Campanas
          </h1>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#64748b",
              background: "#f1f5f9",
              padding: "3px 10px",
              borderRadius: 20,
            }}
          >
            {campaigns.length}
          </span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => navigate("/admin/marketing/group-send")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: "#fff",
              color: "#334155",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#0353a4";
              e.currentTarget.style.color = "#0353a4";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e2e8f0";
              e.currentTarget.style.color = "#334155";
            }}
          >
            <Send size={14} />
            Enviar Campana
          </button>
          <button
            onClick={() => navigate("/campaign/view/new")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              background: "#0353a4",
              color: "#fff",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            <Plus size={14} />
            Nueva Campana
          </button>
        </div>
      </div>

      {/* Campaign grid */}
      {loading ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(480px, 1fr))",
            gap: 20,
          }}
        >
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                ...CARD,
                height: 160,
                background: "#f8fafc",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "80px 0",
            color: "#94a3b8",
          }}
        >
          <Mail size={48} strokeWidth={1} style={{ marginBottom: 12, opacity: 0.5 }} />
          <p style={{ fontSize: 15, fontWeight: 500, margin: 0 }}>No hay campanas creadas</p>
          <p style={{ fontSize: 13, margin: "4px 0 0" }}>Crea tu primera campana para empezar</p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(480px, 1fr))",
            gap: 20,
          }}
        >
          {campaigns.map((c) => (
            <CampaignCard
              key={c._id}
              campaign={c}
              onClick={() => navigate(`/campaign/view/${c._id}`)}
            />
          ))}
        </div>
      )}
    </div>
    </>
  );
}

function CampaignCard({ campaign, onClick }) {
  const [hovered, setHovered] = useState(false);
  const hasImage = campaign.content?.imgUrl;
  const sendLabel = campaign.sendType === "on_demand" ? "Inmediato" : "Programado";
  const statusColor = campaign.enabled ? "#16a34a" : "#94a3b8";

  return (
    <div
      style={{
        ...CARD,
        boxShadow: hovered ? "0 4px 16px rgba(0,0,0,0.08)" : "none",
        transform: hovered ? "translateY(-2px)" : "none",
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ display: "flex", height: "100%" }}>
        {/* Image or placeholder */}
        <div
          style={{
            width: 180,
            minHeight: 180,
            background: hasImage ? `url(${campaign.content.imgUrl}) center/cover` : "#f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {!hasImage && <Mail size={36} color="#cbd5e1" />}
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column" }}>
          <p
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: "#0f172a",
              margin: 0,
              marginBottom: 6,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.3,
            }}
          >
            {campaign.title}
          </p>
          <p
            style={{
              fontSize: 14,
              color: "#64748b",
              margin: 0,
              marginBottom: 16,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.5,
            }}
          >
            {campaign.subject || "Sin asunto"}
          </p>

          <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 10 }}>
            {/* Send type */}
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#64748b",
                background: "#f1f5f9",
                padding: "5px 10px",
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <Calendar size={13} />
              {sendLabel}
            </span>

            {/* Target type */}
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: campaign.targetType === "group" ? "#1d4ed8" : "#7c3aed",
                background: campaign.targetType === "group" ? "#eff6ff" : "#faf5ff",
                padding: "5px 10px",
                borderRadius: 6,
              }}
            >
              {campaign.targetType === "group" ? "Grupo" : "Individual"}
            </span>

            {/* Enabled status */}
            <span
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 13,
                fontWeight: 600,
                color: statusColor,
              }}
            >
              {campaign.enabled ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
              {campaign.enabled ? "Activa" : "Inactiva"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
